// The single data-access contract every screen talks to. Two implementations
// sit behind it — MockLeaveService (in-memory) and GraphLeaveService (live
// SharePoint via Microsoft Graph) — chosen by REACT_APP_DATA_MODE. Screens never
// import either implementation directly; they call createLeaveService().

import type { Client } from "@microsoft/microsoft-graph-client";
import { LeaveSystemConfig } from "../config/leaveSystemConfig";
import {
  CalendarEvent,
  CurrentUser,
  Employee,
  Holiday,
  LeaveBalance,
  LeaveRequest,
  LeaveType,
} from "../models/leaveModels";
import { MockLeaveService } from "./mockLeaveService";
import { GraphLeaveService } from "./graphLeaveService";

export type Decision = "Approved" | "Rejected";

export interface LeaveDataService {
  /** Identity of the signed-in user (Graph /me, or the mock user). */
  getCurrentUser(): Promise<CurrentUser>;
  /** Current user's Entra profile photo as an object URL, or null. Graph only. */
  getMyPhotoUrl?(): Promise<string | null>;
  /** Another user's Entra profile photo (by email) as an object URL, or null. Graph only. */
  getPhotoUrlForEmail?(email: string): Promise<string | null>;
  /** Fetch an attachment (by its SharePoint webUrl) as an object URL for in-app preview. */
  getAttachmentBlobUrl?(webUrl: string): Promise<string | null>;
  getEmployeeByEmail(email: string): Promise<Employee | null>;
  getLeaveTypes(): Promise<LeaveType[]>;
  getHolidays(year: number): Promise<Holiday[]>;
  getMyLeaveRequests(userEmail: string): Promise<LeaveRequest[]>;
  getPendingForManager(managerEmail: string): Promise<LeaveRequest[]>;
  getMyBalances(userEmail: string, year: number): Promise<LeaveBalance[]>;
  /** Files a request; `attachment` (e.g. a medical certificate) is uploaded first. */
  createLeaveRequest(request: LeaveRequest, attachment?: File): Promise<LeaveRequest>;
  /** Manager decision on a pending request (approve/reject). Adjusts balance. */
  decideLeaveRequest(
    requestId: string,
    decision: Decision,
    approverEmail: string,
    notes: string,
  ): Promise<void>;
  /** Employee cancels their own pending/future-approved request; refunds balance. */
  cancelLeaveRequest(requestId: string): Promise<void>;
  getTeamCalendarEvents(startISO: string, endISO: string): Promise<CalendarEvent[]>;

  // --- Mock-only dev affordances (undefined on the live Graph service) ------
  /** List of seed users you can view the demo as. */
  getSwitchableUsers?(): Promise<CurrentUser[]>;
  /** Switch which seed user the app is signed in as. */
  setActingUser?(email: string): void;
}

/**
 * Build the data service for the current mode. Pass the Graph client when
 * available (graph mode); pass null to force mock (or when SSO isn't ready).
 */
export function createLeaveService(graph: Client | null): LeaveDataService {
  if (LeaveSystemConfig.dataMode === "graph" && graph) {
    return new GraphLeaveService(graph);
  }
  return MockLeaveService.instance();
}
